// Camera-enhancement levers (OSPROD-109 v2). Three independent effects,
// Zoom-style: skin smoothing ("Touch up my appearance"), low-light brightness,
// and vibrance (contrast + saturation). Stored as one JSON object of integers
// 0–100. Since OSPROD-232 the source of truth is the user's PROFILE
// (`user.settings.cameraEnhance` — syncs cross-device/room, saved by
// CameraEnhancePicker); localStorage keeps a device-local copy as the
// offline/legacy fallback (resolveEnhance: profile wins, else localStorage).
// All 0 = off (the enhance stage is never engaged → zero cost for non-users).

export const ENHANCE_KEY = 'cameraEnhance'
export const ENHANCE_MIN = 0
export const ENHANCE_MAX = 100

/** The three independent enhancement effects. Values are 0–100 (UI / storage)
 *  or 0..1 (runtime / shader) depending on which reader produced them. */
export interface EnhanceLevels {
  smooth: number
  lowlight: number
  vibrance: number
}

export const ENHANCE_DEFAULT: EnhanceLevels = { smooth: 0, lowlight: 0, vibrance: 0 }

/** Clamp one value to an integer in [0, 100]; non-finite → 0. */
export function clampEnhance(v: number): number {
  if (!Number.isFinite(v)) return 0
  return Math.max(ENHANCE_MIN, Math.min(ENHANCE_MAX, Math.round(v)))
}

function clampLevels(l: Partial<EnhanceLevels> | null | undefined): EnhanceLevels {
  return {
    smooth: clampEnhance(Number(l?.smooth)),
    lowlight: clampEnhance(Number(l?.lowlight)),
    vibrance: clampEnhance(Number(l?.vibrance)),
  }
}

/** Read the persisted levels as 0–100 ints. Migrates the v1 single-slider
 *  format (a bare number) → {smooth: n} so existing users keep their smoothing. */
export function readEnhance(): EnhanceLevels {
  try {
    const raw = localStorage.getItem(ENHANCE_KEY)
    if (raw == null || raw === '') return { ...ENHANCE_DEFAULT }
    // v1 migration: the key used to hold a bare number string.
    const asNum = Number(raw)
    if (Number.isFinite(asNum) && !raw.trim().startsWith('{')) {
      return { smooth: clampEnhance(asNum), lowlight: 0, vibrance: 0 }
    }
    const parsed = JSON.parse(raw)
    if (typeof parsed === 'number') return { smooth: clampEnhance(parsed), lowlight: 0, vibrance: 0 }
    return clampLevels(parsed)
  } catch {
    return { ...ENHANCE_DEFAULT }
  }
}

/** Persist levels (0–100 ints, device-local). */
export function writeEnhance(l: EnhanceLevels): void {
  try {
    localStorage.setItem(ENHANCE_KEY, JSON.stringify(clampLevels(l)))
  } catch {
    /* private mode / storage disabled — non-fatal */
  }
}

/** Levels normalised to [0, 1] for the shader / runtime. */
export function enhanceLevels01(): EnhanceLevels {
  const l = readEnhance()
  return { smooth: l.smooth / ENHANCE_MAX, lowlight: l.lowlight / ENHANCE_MAX, vibrance: l.vibrance / ENHANCE_MAX }
}

/** Profile-first read (OSPROD-232): `user.settings.cameraEnhance` (0–100 ints,
 *  synced cross-device via PUT /auth/me) wins; the device-local localStorage
 *  value is the offline/legacy fallback. Returns 0–100 ints. */
export function resolveEnhance(settings?: Record<string, unknown> | null): EnhanceLevels {
  const s = (settings as any)?.cameraEnhance
  if (s && typeof s === 'object') return clampLevels(s)
  return readEnhance()
}

/** resolveEnhance normalised to [0, 1] for the shader / runtime. */
export function resolveEnhance01(settings?: Record<string, unknown> | null): EnhanceLevels {
  const l = resolveEnhance(settings)
  return { smooth: l.smooth / ENHANCE_MAX, lowlight: l.lowlight / ENHANCE_MAX, vibrance: l.vibrance / ENHANCE_MAX }
}

/** True when any effect is turned up (used to decide whether to engage the stage). */
export function anyEnhanceOn(l: EnhanceLevels): boolean {
  return l.smooth > 0 || l.lowlight > 0 || l.vibrance > 0
}
